import * as React from 'react';

import styled from '@emotion/styled';
import Group, { GroupAddon } from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import { colors } from '@lightspeed/cirrus-tokens';
import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { IconClose } from '@lightspeed/flame/Icon/Close';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Select } from '@lightspeed/flame/Select';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Heading2, Heading3, Text, TextLink } from '@lightspeed/flame/Text';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import { PAYMENT_STATUS, TERMINAL_EVENT } from '@lightspeed-payments/payments-terminal-sdk';
import Decimal from 'app/src/data/base/Decimal';
import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import SaleManager from 'app/src/payments/SaleManager';
import { POLLING_EVENT } from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal from 'app/src/payments/terminals/Terminal';
import PaymentServiceAlert from 'root/apps/app/src/controllers/payments/PaymentServiceAlert';
import { validateMinAmount } from 'root/apps/app/src/payments/amount-validation';
import { trackAnalyticsEvent } from 'root/apps/helpers/AnalyticsTracking';
import { isTerminalTypeDeprecated } from 'root/apps/shared_modules/Terminals';
import Modules from 'shared_modules/Modules';

import PreauthDisclaimer from './PreauthDisclaimer';

export const TransactionState = {
  CANCEL_FROM_TERMINAL: 'cancel_from_terminal',
  CANCELED: 'transaction_canceled',
  CANCELLED_FROM_POS: 'transaction_canceled_pos',
  CANCEL_FROM_POS_UNREFERENCED_REFUND: 'cancel_from_pos_unreferenced_refund',
  CANCEL_UNAVAILABLE: 'transaction_cancellation_failed',
  NON_CANCELLABLE_STATE: 'transaction_cancellation_not_possible',
  COMPLETE: 'transaction_complete',
  PRE_AUTHORIZED: 'pre-authorized',
  DECLINED: 'transaction_declined',
  ERROR: 'error',
  FAILED: 'transaction_failed',
  IN_PROGRESS: 'in_progress',
  TERMINAL_ERROR: 'terminal_error',
  SERVICE_EXCEPTION: 'service_exception',
  WAITING_TO_START: 'waiting_to_start',
  CONNECTION_DEGRADED: 'connection_degraded',
  CONNECTION_KO: 'connection_ko',
};

export const STRINGS = {
  CONNECTIVITY: {
    [TransactionState.CONNECTION_DEGRADED]: {
      title: `We're having trouble connecting with the server`,
      message: `Please wait a few seconds while we try to reconnect`,
    },
    [TransactionState.CONNECTION_KO]: {
      title: `Connectivity issue`,
      message: `We are currently unable to re-establish a connection.\nBefore retrying the transaction, check your terminal to verify if the payment was processed.`,
    },
  },
};

const WarningWrapper = styled('div')`
  position: relative;
`;

const WarningIcon = styled('div')`
  position: absolute;
  top: -16px; // Half-Size of the icon
  right: -32px; // Size of the icon
`;

const PreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

class TerminalPaymentModal extends React.Component {
  constructor(props) {
    super(props);
    const supportedFeatures = PaymentProvider.supportedFeaturesForGateway(
      get(this.props.paymentProvider, 'activeGateway.attributes', {})
    );

    trackAnalyticsEvent({
      eventCategory: 'QuickPay',
      eventName: 'QuickPay Clicked',
      eventAction: 'Sale Started',
      eventLabel: 'QuickPay-V1',
    });

    this.state = {
      isOpen: true,
      isLoading: false,
      amount: props.initialAmount,
      transactionState: TransactionState.WAITING_TO_START,
      supportedFeatures,
      selectedTerminal:
        props.terminal ||
        props.paymentProvider.getTerminal(CommonData.selectedRegister.get('ccTerminalID')),
      payment: null,
      transactionMessage: null,
      syncingWithServer: false,
      disableCancelButton: false,
      showRetryButton: true,
      timeStart: Date.now(),
    };

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleStartPaymentClicked = this.handleStartPaymentClicked.bind(this);
    this.handleRetryPaymentClicked = this.handleRetryPaymentClicked.bind(this);
    this.handleCancelClicked = this.handleCancelClicked.bind(this);
    this.handleCloseClicked = this.handleCloseClicked.bind(this);
    this.handleTerminalSelected = this.handleTerminalSelected.bind(this);
    this.handleAmountChange = this.handleAmountChange.bind(this);
    this.handlePaymentComplete = this.handlePaymentComplete.bind(this);
    this.handleCancelWhileInProgress = this.handleCancelWhileInProgress.bind(this);
    this.handleBackToInProgress = this.handleBackToInProgress.bind(this);
    this.printReceipt = this.printReceipt.bind(this);
    this.handleEnterKeyPress = this.handleEnterKeyPress.bind(this);
    this.handlePollingStatusUpdate = this.handlePollingStatusUpdate.bind(this);
    this.handleTransactionEvents = this.handleTransactionEvents.bind(this);
    this.handleBackInProgress = this.handleBackInProgress.bind(this);
    this.handleSaleRefreshingBeforeStartingPayment =
      this.handleSaleRefreshingBeforeStartingPayment.bind(this);
    this.handleSaleRefreshedBeforeStartingPayment =
      this.handleSaleRefreshedBeforeStartingPayment.bind(this);
  }

  /**
   * Handles closing the modal.  Since we have closing animations, we need to make
   * sure we wait for that to finish before we unmount otherwise we'll get an
   * exception thrown by React.
   */
  // eslint-disable-next-line react/sort-comp
  handleCloseModal() {
    trackAnalyticsEvent({
      eventCategory: 'QuickPay',
      eventName: 'QuickPay Clicked',
      eventAction: 'Modal Closed',
      eventValue: `${Math.abs((Date.now() - this.state.timeStart) / 1000).toString()}seconds`,
      eventLabel: 'QuickPay-V1',
    });
    this.setState(
      {
        isOpen: false,
      },
      () => {
        this.props.onHide({
          payment: this.state.payment,
          transactionState: this.state.transactionState,
        });
      }
    );
  }

  /**
   * Handler for when a terminal is selected.
   *
   * @param {Event} event The select event
   */
  handleTerminalSelected(event) {
    // Just track those without the quickpayV2 module
    if (!Modules.hasModule('gui.payment.quickpayV2')) {
      const { name, openEmployeeID, shopID } = CommonData.selectedRegister.attributes;

      trackAnalyticsEvent({
        eventCategory: 'QuickPay',
        eventName: 'QuickPay Terminal Selection',
        eventAction: 'Terminal Switched',
        eventValue: {
          registerName: name,
          shopID,
          openEmployeeID,
        },
        eventLabel: 'QuickPay-V1',
      });
    }

    const terminalID = event.target.value;
    const { paymentProvider } = this.props;

    CommonData.selectedRegister.set('ccTerminalID', terminalID).save();

    this.setState(() => ({
      selectedTerminal: paymentProvider.getTerminal(terminalID),
    }));
  }

  handleAmountChange(event) {
    this.setState({
      amount: new Decimal(event.target.value),
    });
  }

  /**
   * Invokes the onFinishSale prop method and then closes the modal.
   */
  handlePaymentComplete() {
    const { onPaymentComplete } = this.props;
    this.setState({
      syncingWithServer: true,
    });

    onPaymentComplete(this.state.payment)
      .then(() => {
        this.setState({
          syncingWithServer: false,
        });
        this.handleCloseModal();
      })
      .catch(() => {
        // errors are displayed in the legacy UI so just close the modal
        this.setState({
          syncingWithServer: false,
        });
        this.handleCloseModal();
      });
  }

  // used for quick pay
  handleStartPaymentClicked() {
    const { analyticsName, saleManager, paymentProvider, isManualEntry, isUnreferencedRefund } =
      this.props;
    const { selectedTerminal } = this.state;

    try {
      validateMinAmount(
        saleManager.pendingBalance,
        window.merchantos?.locale?.currency_code,
        paymentProvider.isGatewayLightspeedPayments
      );
    } catch (error) {
      this.handleCloseModal();
      PaymentServiceAlert.alert(error.message);
      return;
    }

    if (paymentProvider.isGatewayLightspeedPayments && selectedTerminal?.hasMonitoringModule) {
      selectedTerminal.startLspayMonitoring(paymentProvider.activeGatewayPli, {
        isRetry: false,
        isQuickPay: true,
        isUnreferencedRefund,
        isManualEntry,
      });
    }

    PaymentAnalytics.trackGenericPaymentEvent(analyticsName, 'Started', saleManager.sale.id, {
      paymentProcessor: paymentProvider.activeGatewayProcessorType,
    });

    this.handleStartTransaction();
  }

  handleRetryPaymentClicked() {
    const { analyticsName, saleManager, paymentProvider, isManualEntry, isUnreferencedRefund } =
      this.props;
    const { selectedTerminal } = this.state;

    if (paymentProvider.isGatewayLightspeedPayments && selectedTerminal?.hasMonitoringModule) {
      selectedTerminal.startLspayMonitoring(paymentProvider.activeGatewayPli, {
        isRetry: true,
        isQuickPay: true,
        isUnreferencedRefund,
        isManualEntry,
      });
    }

    PaymentAnalytics.trackGenericPaymentEvent(analyticsName, 'Retry', saleManager.sale.id, {
      paymentProcessor: paymentProvider.activeGatewayProcessorType,
    });

    PaymentOperations.abandonTerminalPaymentOperation({ terminal: selectedTerminal });
    this.handleStartTransaction();
  }

  handleCancelClicked() {
    const { analyticsName, saleManager, paymentProvider } = this.props;

    PaymentAnalytics.trackGenericPaymentEvent(analyticsName, 'Cancel', saleManager.sale.id, {
      paymentProcessor: paymentProvider.activeGatewayProcessorType,
    });

    this.handleCloseModal();
  }

  handleCloseClicked() {
    const { analyticsName, saleManager, paymentProvider } = this.props;
    const { selectedTerminal } = this.state;

    PaymentAnalytics.trackGenericPaymentEvent(analyticsName, 'Close', saleManager.sale.id, {
      paymentProcessor: paymentProvider.activeGatewayProcessorType,
    });

    PaymentOperations.abandonTerminalPaymentOperation({ terminal: selectedTerminal });
    this.handleCloseModal();
  }

  handleBackInProgress() {
    this.setState(() => ({
      transactionState: TransactionState.IN_PROGRESS,
    }));
  }

  /**
   * Handles initiating a transaction
   */
  handleStartTransaction() {
    const {
      saleManager,
      paymentProvider,
      recoveredPayment,
      isManualEntry,
      isUnreferencedRefund,
      refundedPaymentID,
      tipAmount = null,
    } = this.props;
    const { amount, selectedTerminal } = this.state;

    let transactionState = TransactionState.IN_PROGRESS;
    if (
      isUnreferencedRefund &&
      paymentProvider.activeGateway.gatewayID === GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
    ) {
      transactionState = TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND;
    }

    this.setState(() => ({
      payment: null,
      transactionState,
      transactionMessage: null,
      disableCancelButton: this.apiCanCancel,
    }));

    this.handlePaymentOperation(
      PaymentOperations.doTerminalPaymentOperation({
        amount,
        terminal: selectedTerminal,
        saleManager,
        gatewayConfig: paymentProvider.activeGateway,
        recoveredPayment,
        isManualEntry,
        isUnreferencedRefund,
        refundedPaymentID,
        ...(tipAmount && { tipAmount }),
        ...(this.props.captureMethod && { captureMethod: this.props.captureMethod }),
      })
    );
  }

  /**
   * Sets the modal state to the cancel while in progress state.
   */
  handleCancelWhileInProgress() {
    if (this.apiCanCancel) {
      const { selectedTerminal, payment } = this.state;

      PaymentOperations.cancelPayment({ terminal: selectedTerminal, payment }).catch((response) => {
        const { response: res } = response || { response: '' };
        switch (res) {
          case Terminal.Response.NON_CANCELLABLE_STATE:
            this.setState(() => ({
              transactionState: TransactionState.NON_CANCELLABLE_STATE,
              disableCancelButton: false,
            }));
            break;
          case Terminal.Response.CANCEL_UNAVAILABLE:
            this.setState(() => ({
              transactionState: TransactionState.CANCEL_UNAVAILABLE,
              disableCancelButton: false,
            }));
            break;
          default:
            this.setState(() => ({
              transactionState: TransactionState.SERVICE_EXCEPTION,
            }));
        }
      });
    } else {
      this.setState(() => ({
        transactionState: TransactionState.CANCEL_FROM_TERMINAL,
      }));
    }
  }

  /**
   * Sets the modal state to the in progress state.
   */
  handleBackToInProgress() {
    this.setState(() => ({
      transactionState: TransactionState.IN_PROGRESS,
    }));
  }

  /**
   * Handles the complexity of disabling the cancel button
   * Cancel is disabled if the gateways supports Cancel and if it's not an unreferencedRefund
   * OR if the gateway is lightspeed payments and supports cancel
   */
  get apiCanCancel() {
    return Boolean(
      this.state.supportedFeatures.supportApiCancel &&
        (!this.props.isUnreferencedRefund || this.props.paymentProvider.isGatewayLightspeedPayments)
    );
  }

  /**
   * Handles the response from a terminal transaction.
   *
   * @param {Deferred.Promise} promise The promise which resolves/fails when the transaction is finished.
   */
  handlePaymentOperation(promise) {
    const { analyticsName, saleManager, paymentProvider } = this.props;

    promise
      .done((response) => {
        switch (response.response) {
          case Terminal.Response.APPROVED:
          case Terminal.Response.PAYMENT_ALREADY_RECONCILED:
          case Terminal.Response.PRE_AUTHORIZED:
            PaymentAnalytics.trackGenericPaymentEvent(
              analyticsName,
              'Approved',
              saleManager.sale.id,
              {
                salePaymentID: response.payment.model.id,
                paymentID: response.payment.serviceModel.id,
                paymentProcessor: paymentProvider.activeGatewayProcessorType,
              }
            );
            trackAnalyticsEvent({
              eventCategory: 'QuickPay',
              eventName: 'QuickPay Clicked',
              eventAction: 'Payment Completed successfully',
              eventValue: `${Math.abs(
                (Date.now() - this.state.timeStart) / 1000
              ).toString()}seconds`,
              eventLabel: 'QuickPay-V1',
            });

            this.setState(() => ({
              payment: response.payment,
              transactionState:
                Modules.hasModule('payments.pre_auth') &&
                response.response === Terminal.Response.PRE_AUTHORIZED
                  ? TransactionState.PRE_AUTHORIZED
                  : TransactionState.COMPLETE,
            }));
            break;

          default:
            this.setState(() => ({
              transactionState: TransactionState.ERROR,
              transactionMessage: 'An unexpected error has occurred',
            }));
            break;
        }
      })
      .fail((response) => {
        trackAnalyticsEvent({
          eventCategory: 'QuickPay',
          eventName: 'QuickPay Clicked',
          eventAction: 'Payment Failed',
          eventValue: `${Math.abs((Date.now() - this.state.timeStart) / 1000).toString()}seconds`,
          eventLabel: 'QuickPay-V1',
        });
        const isLspayLocalCancelledUnreferenced =
          this.props.paymentProvider.isGatewayLightspeedPayments &&
          this.props.isUnreferencedRefund &&
          !response.payment &&
          response.status === Terminal.Response.CANCELLED &&
          Modules.hasModule('payments.clientSide.CancelPayment');

        if (isLspayLocalCancelledUnreferenced) {
          PaymentAnalytics.trackGenericPaymentEvent(
            analyticsName,
            'Canceled',
            saleManager.sale.id,
            {
              salePaymentID: 'N/A',
              paymentID: 'N/A',
              message: 'clientSide unreferenced refund canceled from POS',
              paymentProcessor: paymentProvider.activeGatewayProcessorType,
            }
          );
          this.setState(() => ({
            transactionState: TransactionState.CANCELLED_FROM_POS,
            transactionMessage: response.message,
          }));
          return;
        }

        const message =
          get(response, 'payment.serviceModel.paymentTransaction.parameters.message') ||
          response.error;

        const detailedErrorMessage =
          get(response, 'payment.serviceModel.paymentTransaction.parameters.detailedMessage') ||
          null;

        switch (response.response) {
          case Terminal.Response.CANCELLED_FROM_TERMINAL:
          case Terminal.Response.CANCELLED:
            PaymentAnalytics.trackGenericPaymentEvent(
              analyticsName,
              'Canceled',
              saleManager.sale.id,
              {
                salePaymentID: response.payment.model.id,
                paymentID: response.payment.serviceModel.id,
                paymentProcessor: paymentProvider.activeGatewayProcessorType,
              }
            );

            this.setState(() => ({
              payment: response.payment,
              transactionState: TransactionState.CANCELED,
            }));
            break;

          case Terminal.Response.DECLINED:
            PaymentAnalytics.trackGenericPaymentEvent(
              analyticsName,
              'Declined',
              saleManager.sale.id,
              {
                salePaymentID: response.payment.model.id,
                paymentID: response.payment.serviceModel.id,
                paymentProcessor: paymentProvider.activeGatewayProcessorType,
              }
            );

            this.setState(() => ({
              payment: response.payment,
              transactionState: TransactionState.DECLINED,
              transactionMessage: detailedErrorMessage
                ? `${message} - ${detailedErrorMessage}`
                : message,
            }));
            break;
          case Terminal.Response.CANCELLED_FROM_POS:
            PaymentAnalytics.trackGenericPaymentEvent(
              analyticsName,
              'Canceled',
              saleManager.sale.id,
              {
                salePaymentID: response.payment ? response.payment.model.id : 'N/A',
                paymentID: response.payment ? response.payment.serviceModel.id : 'N/A',
                paymentProcessor: paymentProvider.activeGatewayProcessorType,
              }
            );

            this.setState(() => ({
              payment: response.payment,
              transactionState: TransactionState.CANCELLED_FROM_POS,
              transactionMessage: detailedErrorMessage
                ? `${message} - ${detailedErrorMessage}`
                : message,
            }));
            break;
          case Terminal.Response.ERROR:
          case Terminal.Response.TERMINAL_ERROR:
          case Terminal.Response.TIMEOUT:
            PaymentAnalytics.trackGenericPaymentEvent(analyticsName, 'Error', saleManager.sale.id, {
              salePaymentID: response.payment ? response.payment.model.id : 'N/A',
              paymentID: response.payment ? response.payment.serviceModel.id : 'N/A',
              message,
              detailedErrorMessage,
              paymentProcessor: paymentProvider.activeGatewayProcessorType,
            });

            const transactionMessage = detailedErrorMessage
              ? `${message} - ${detailedErrorMessage}`
              : message;

            this.setState(() => ({
              payment: response.payment,
              transactionState: TransactionState.ERROR,
              transactionMessage,
            }));

            if (
              transactionMessage &&
              transactionMessage.includes(
                'The selected terminal is no longer paired to this account'
              )
            ) {
              this.setState(() => ({
                showRetryButton: false,
              }));
            }
            break;

          case Terminal.Response.SERVICE_EXCEPTION:
            this.setState(() => ({
              payment: response.payment,
              transactionState: TransactionState.SERVICE_EXCEPTION,
              transactionMessage: detailedErrorMessage
                ? `${message} - ${detailedErrorMessage}`
                : message,
            }));
            break;

          default:
            this.setState(() => ({
              transactionState: TransactionState.ERROR,
              transactionMessage: 'An unknown error has occurred.',
            }));
            break;
        }
      })
      .always((response) => {
        const { selectedTerminal: terminal } = this.state;

        if (terminal?.lspayMonitoring) {
          const { payment, terminalMonitoringStatus } = response;

          const reasonPrefix = response.responseCode ?? response.code ?? '';
          const reasonSuffix =
            get(response, 'payment.serviceModel.paymentTransaction.attributes.message') ||
            response.error ||
            response.message ||
            '';

          terminal.lspayMonitoring.sendEvent(TERMINAL_EVENT.COMPLETE, {
            payment: {
              id: payment?.serviceModel?.id,
              amount: payment?.amount?.toNumber(),
              currency: window.merchantos?.locale?.currency_code,
              status: !payment ? PAYMENT_STATUS.ERROR : PAYMENT_STATUS[terminalMonitoringStatus],
              reason:
                `${
                  reasonPrefix && reasonSuffix
                    ? `${reasonPrefix} - ${reasonSuffix}`
                    : reasonPrefix || reasonSuffix
                }` || undefined,
              ...(response instanceof Error && {
                error: response,
              }),
            },
            terminalTransactionResponse: response,
          });
        }

        // If the payment is complete, just finish the modal state
        if (
          ((this.state.transactionState === TransactionState.COMPLETE ||
            this.state.transactionState === TransactionState.PAYMENT_ALREADY_RECONCILED) &&
            Modules.hasModule('gui.payment.quickpayV2')) ||
          (this.state.transactionState === TransactionState.PRE_AUTHORIZED &&
            Modules.hasModule('payments.pre_auth'))
        ) {
          this.handlePaymentComplete();
        }
      });
  }

  handlePollingStatusUpdate({ detail }) {
    switch (detail) {
      case POLLING_EVENT.STATUS.DEGRADED:
        this.setState({ transactionState: TransactionState.CONNECTION_DEGRADED });
        break;
      case POLLING_EVENT.STATUS.KO:
        this.setState({ transactionState: TransactionState.CONNECTION_KO });
        break;
      case POLLING_EVENT.STATUS.OK:
        this.setState({ transactionState: TransactionState.IN_PROGRESS });
        break;
      default:
        throw new Error(`Unhandled polling state ${detail}`);
    }
  }

  /**
   * Prints the receipt for the last failed/cancelled transaction.
   */
  printReceipt() {
    const { payment } = this.state;
    window.merchantos.print.printUrl({
      url: `/API/Account/${window.merchantos.session.getSystemCustomerID()}/DisplayTemplate/SalePayment/${payment.model.get(
        'salePaymentID'
      )}.html?template=SalePayment&print=1&page_width=auto&page_height=2000mm`,
      pageType: 'receipt',
    });
  }

  /**
   * Called by React when the Modal is mounted.
   */
  // eslint-disable-next-line react/sort-comp
  componentDidMount() {
    const { terminal, initialAmount, recoveredPayment, saleManager } = this.props;

    window.addEventListener('keypress', this.handleEnterKeyPress);
    window.addEventListener(POLLING_EVENT.NAME, this.handlePollingStatusUpdate);
    window.addEventListener(Terminal.TerminalEvents.PAYMENT_STARTED, this.handleTransactionEvents);
    window.addEventListener(Terminal.TerminalEvents.CANCEL_STARTED, this.handleTransactionEvents);
    window.addEventListener(
      SaleManager.Events.SALE_REFRESHING,
      this.handleSaleRefreshingBeforeStartingPayment
    );
    window.addEventListener(
      SaleManager.Events.SALE_REFRESHED,
      this.handleSaleRefreshedBeforeStartingPayment
    );

    if ((terminal && initialAmount) || recoveredPayment) {
      // we have everything we need to start the transaction
      this.handleStartTransaction();
    }

    // If the saleManager is already updating we will set the isLoading state accordingly
    if (saleManager.isSaleUpdating) {
      this.setState({ isLoading: saleManager.isSaleUpdating });
    }
  }

  /**
   * Called by React when the Modal is unmounted
   */
  // eslint-disable-next-line react/sort-comp
  componentWillUnmount() {
    window.removeEventListener(POLLING_EVENT.NAME, this.handlePollingStatusUpdate);
    window.removeEventListener('keypress', this.handleEnterKeyPress);
    window.removeEventListener(
      Terminal.TerminalEvents.PAYMENT_STARTED,
      this.handleTransactionEvents
    );
    window.removeEventListener(
      Terminal.TerminalEvents.CANCEL_STARTED,
      this.handleTransactionEvents
    );
    window.removeEventListener(
      SaleManager.Events.SALE_REFRESHING,
      this.handleSaleRefreshingBeforeStartingPayment
    );
    window.removeEventListener(
      SaleManager.Events.SALE_REFRESHED,
      this.handleSaleRefreshedBeforeStartingPayment
    );
  }

  /**
   * Used for handling the SALE_UPDATING event
   * which update the internal state isLoading accordingly
   *
   * This method will only update loading state
   * when we're waiting to start the payment.
   */
  handleSaleRefreshingBeforeStartingPayment() {
    const { transactionState } = this.state;
    if (transactionState === TransactionState.WAITING_TO_START) {
      this.setState({ isLoading: true });
    }
  }

  /**
   * Used for handling the SALE_REFRESHED event
   * which update the amount that need to be processed
   * and set the isLoading state to false.
   *
   * This method will only update loading state and the amount
   * when we're waiting to start the payment.
   */
  handleSaleRefreshedBeforeStartingPayment() {
    const { saleManager } = this.props;
    const { transactionState } = this.state;

    if (transactionState === TransactionState.WAITING_TO_START) {
      this.setState({ amount: saleManager.pendingBalance, isLoading: false });
    }
  }

  /*
   * Used for handling communication between react and backbone.
   * handleTransactionStarted is called only for gateways that support API cancel
   * Terminal.TerminalEvents.PAYMENT_STARTED is fired by stripe terminal  on waiting for input
   * LSP terminal fire them when the payment has been staged
   */
  handleTransactionEvents({ detail = {} }) {
    const { payment, disableCancelButton } = detail;
    if (this.apiCanCancel) {
      if (payment) {
        this.setState({
          disableCancelButton: false,
          payment,
        });
      }
      if (typeof disableCancelButton === 'boolean') {
        this.setState({
          disableCancelButton,
        });
      }
    }
  }

  /**
   * Called when an enter key is pressed
   *
   * @param {DOMEvent} event The triggering event
   */
  handleEnterKeyPress(event) {
    if (event.key === 'Enter') {
      const { selectedTerminal, transactionState } = this.state;

      switch (transactionState) {
        case TransactionState.WAITING_TO_START:
          if (selectedTerminal) {
            this.handleStartPaymentClicked();
          }
          break;

        case TransactionState.CANCELED:
        case TransactionState.CANCELLED_FROM_POS:
        case TransactionState.DECLINED:
        case TransactionState.FAILED:
        case TransactionState.ERROR:
          this.handleRetryPaymentClicked();
          break;

        case TransactionState.COMPLETE:
          this.handlePaymentComplete();
          break;

        case TransactionState.PRE_AUTHORIZED:
          if (Modules.hasModule('payments.pre_auth')) {
            this.handlePaymentComplete();
          }
          break;

        default:
          break;
      }
    }
  }

  /**
   * Renders the Modal header
   */
  renderHeader() {
    const { selectedTerminal, transactionState } = this.state;
    const { captureMethod } = this.props;
    let title = 'No Title';

    switch (transactionState) {
      case TransactionState.WAITING_TO_START:
        title = 'Card Payment';
        break;

      default:
        // Use "Process Preauthorization" for manual capture (preauth), otherwise use terminal name
        title =
          Modules.hasModule('payments.pre_auth') && captureMethod === 'manual'
            ? 'Process Preauthorization'
            : selectedTerminal.model.get('name');
        break;
    }

    return (
      <ModalHeader showCloseButton={false} onCloseClicked={this.handleCloseModal}>
        <div className="quick-pay-modal__header">{title}</div>
      </ModalHeader>
    );
  }

  /**
   * Renders the Modal body.  What is rendered is dependent on the modal's transactional state.
   */
  renderBodyMain() {
    const { paymentProvider, canAmountBeChanged } = this.props;
    const { amount, selectedTerminal, transactionState, transactionMessage, isLoading } =
      this.state;
    const selectedTerminalId = selectedTerminal ? selectedTerminal.model.id : '';
    const bodySupplemental = this.renderBodySupplemental();
    const { colorsMap } = colors;

    let bodyMain = null;

    if (transactionState === TransactionState.WAITING_TO_START) {
      const terminalDropdownData = paymentProvider.terminalCollection.map((terminal) => ({
        value: terminal.id,
        label: terminal.get('name'),
      }));

      bodyMain = (
        <div className="quick-pay-modal__body cirrus">
          <label htmlFor="quick-pay-terminal-select">
            <Text bodySmall fontWeight="bold">
              Terminal
            </Text>
          </label>

          <Select
            id="quick-pay-terminal-select"
            name="terminal"
            disabled={isLoading}
            placeholder="Select a Terminal"
            value={selectedTerminalId}
            onChange={this.handleTerminalSelected}
          >
            <option disabled={!!selectedTerminal}>Select a Terminal</option>
            {terminalDropdownData.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <div className="quick-pay-modal__amount">
            <label htmlFor="amount">
              <Text bodySmall fontWeight="bold">
                Amount
              </Text>
            </label>
            <Group noSpacing inputBlock>
              <GroupAddon>{Localize.getCurrencySymbol()}</GroupAddon>
              <Input
                id="amount"
                name="amount"
                value={isLoading ? '' : Format.formatMoney(amount, 0, false)}
                onChange={this.handleAmountChange}
                disabled={!canAmountBeChanged}
                prefix={isLoading && <Spinner color={colorsMap['gray-200']} />}
              />
            </Group>
          </div>
          {bodySupplemental}
        </div>
      );
    } else {
      let image = null;
      let title = null;
      let message = null;

      switch (transactionState) {
        case TransactionState.IN_PROGRESS:
          image = <Spinner color="blue-300" baseColor2="gray-200" size="96px" />;
          title = 'Complete payment on terminal';
          message = 'Ask the client to follow the instructions on the terminal';
          break;

        case TransactionState.CONNECTION_DEGRADED:
          image = (
            <WarningWrapper>
              <Spinner color="blue-300" baseColor2="gray-200" size="96px" />
              <WarningIcon>
                <IconWarning baseColor1="orange" detailsColor1="gray-800" size="32px" />
              </WarningIcon>
            </WarningWrapper>
          );
          title = STRINGS.CONNECTIVITY[transactionState].title;
          message = STRINGS.CONNECTIVITY[transactionState].message;
          break;

        case TransactionState.CONNECTION_KO:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = STRINGS.CONNECTIVITY[transactionState].title;
          message = STRINGS.CONNECTIVITY[transactionState].message;
          break;

        case TransactionState.COMPLETE:
          const { payment } = this.state;
          // eslint-disable-next-line no-shadow
          const isTerminalTippingEnabled = Modules.hasModule('payments.terminal_tipping') ?? false;
          const isPartiallyApproved = isTerminalTippingEnabled
            ? payment.amount.isLessThan(amount)
            : !payment.amount.isEqualTo(amount);

          image = (
            <WarningWrapper>
              <IconVerified size="96px" color="green-700" />
              {isPartiallyApproved && (
                <WarningIcon>
                  <IconWarning baseColor1="orange" detailsColor1="gray-800" size="32px" />
                </WarningIcon>
              )}
            </WarningWrapper>
          );

          title = isPartiallyApproved ? 'Transaction Partially Approved' : 'Payment Approved';
          break;

        case TransactionState.PRE_AUTHORIZED:
          if (Modules.hasModule('payments.pre_auth')) {
            image = <IconVerified size="96px" color="green-700" />;
            title = 'Preauthorization Approved';
          }
          break;

        case TransactionState.CANCELED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Payment canceled';
          message = 'The payment was canceled on the terminal.';
          break;
        case TransactionState.CANCELLED_FROM_POS:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Payment canceled';
          message =
            transactionMessage &&
            transactionMessage !== '' &&
            transactionMessage !== 'The transaction has been cancelled.'
              ? transactionMessage
              : 'You successfully canceled the payment.';
          break;
        case TransactionState.CANCEL_UNAVAILABLE:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'This transaction cannot be canceled right now';
          message = 'Give it a few seconds and try again.';
          break;
        case TransactionState.NON_CANCELLABLE_STATE:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'The transaction can not be cancelled';
          message = 'It is in a non-cancellable state.';
          break;
        case TransactionState.DECLINED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Payment Declined';
          message = transactionMessage;
          break;

        case TransactionState.FAILED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Payment Failed';
          break;

        case TransactionState.ERROR:
        case TransactionState.TERMINAL_ERROR:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Payment Error';
          message = transactionMessage;
          break;

        case TransactionState.CANCEL_FROM_TERMINAL:
          image = (
            <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/cancelFromGeniusTerminal@2x.png" title="terminal" alt="" />
          );
          break;

        case TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND:
          image = <Spinner color="blue-300" baseColor2="gray-200" size="96px" />;
          title = 'Complete refund on terminal';
          message = 'Ask the client to follow the instructions on the terminal';
          break;

        case TransactionState.SERVICE_EXCEPTION:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          title = 'Please contact support';
          message = transactionMessage;
          break;

        default:
          break;
      }

      const isError = [TransactionState.ERROR, TransactionState.TERMINAL_ERROR].includes(
        transactionState
      );

      bodyMain = (
        <div className="quick-pay-modal__body">
          {Modules.hasModule('payments.deprecated_terminal') &&
            isTerminalTypeDeprecated(selectedTerminal?.terminalModel?.attributes?.type) &&
            isError && (
              <Alert data-test="deprecated-terminal-error-alert" type="danger" noCloseBtn mb={2}>
                This terminal is no longer supported and may cause issues taking payments.{' '}
                <TextLink
                  href="https://retail-support.lightspeedhq.com/hc/en-us/articles/360038226154-Setting-up-the-Verifone-P400-with-Lightspeed-Payments"
                  target="_blank"
                >
                  Learn more
                </TextLink>{' '}
                about upgrading to faster, more reliable terminal.
              </Alert>
            )}
          <div className="quick-pay-modal__body__icon">{image}</div>
          <div className="quick-pay-modal__body__message">
            {title && <Heading3 mt="2">{title}</Heading3>}
            {message && (
              <PreWrapText mt="2" size="small" color="gray-600">
                {message}
              </PreWrapText>
            )}
          </div>

          {bodySupplemental}
        </div>
      );
    }

    return bodyMain;
  }

  /**
   * Renders supplemental information about the transaction.  What is rendered is dependent on the modal's
   * transactional state.
   */
  renderBodySupplemental() {
    // eslint-disable-next-line no-unused-vars
    const { amount, transactionState, isLoading } = this.state;
    const { captureMethod, paymentProvider } = this.props;
    const { colorsMap } = colors;

    switch (transactionState) {
      case TransactionState.WAITING_TO_START:
        return (
          <div className="quick-pay-modal__body__footer quick-pay-modal__body__footer__row">
            <Text bodySmall fontWeight="bold">
              Balance
            </Text>
            {isLoading ? <Bone width="50%" height={4} /> : <div>{Format.formatMoney(amount)}</div>}
          </div>
        );

      case TransactionState.IN_PROGRESS:
      case TransactionState.CONNECTION_DEGRADED:
        return (
          <div className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered">
            <Heading2 className="quick-pay-modal__body__footer--total">
              {Format.formatMoney(amount, 0)}
            </Heading2>
            <PreauthDisclaimer paymentProvider={paymentProvider} captureMethod={captureMethod} />
          </div>
        );

      case TransactionState.COMPLETE:
        // eslint-disable-next-line no-shadow
        const { payment } = this.state;
        const isPartiallyApproved = !payment.amount.isEqualTo(amount);
        return (
          <div className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered">
            {isPartiallyApproved && (
              <div className="quick-pay-modal__body__footer--strikethrough">
                {Format.formatMoney(amount, 0)}
              </div>
            )}
            <Heading2 className="quick-pay-modal__body__footer--total">
              {Format.formatMoney(payment.amount, 0)}
            </Heading2>
          </div>
        );
      case TransactionState.CANCELED:
      case TransactionState.DECLINED:
        if (this.props.paymentProvider.isPlatformV3()) return null;
        return (
          <div className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered">
            <Button onClick={this.printReceipt}>
              <IconPrinters color={colorsMap['gray-200']} />
              <span>Print Receipt</span>
            </Button>
          </div>
        );

      case TransactionState.CANCEL_FROM_TERMINAL:
        return (
          <div
            data-test="cancel-from-terminal"
            className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered"
          >
            Payment must be canceled on the terminal.
          </div>
        );

      case TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND:
        return (
          <div
            data-test="cancel-from-terminal"
            className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered"
          >
            <Heading2 className="quick-pay-modal__body__footer--total">
              {Format.formatMoney(amount, 0)}
            </Heading2>
          </div>
        );

      default:
        return null;
    }
  }

  /**
   * Renders the modal's body contents.
   */
  renderBody() {
    const bodyMain = this.renderBodyMain();

    return <ModalBody>{bodyMain}</ModalBody>;
  }

  /**
   * Renders the modal's footer.  What is rendered is dependent on the modal's transactional state.
   */
  renderFooter() {
    const { autoFinishSale } = this.props;
    const { amount, selectedTerminal, transactionState, syncingWithServer } = this.state;

    let footer = null;

    switch (transactionState) {
      case TransactionState.CONNECTION_DEGRADED:
      case TransactionState.IN_PROGRESS:
        footer = (
          <div className="quick-pay-modal__footer quick-pay-modal__footer--centered">
            <Group>
              <Button
                data-test="cancel-btn"
                disabled={this.state.disableCancelButton}
                onClick={this.handleCancelWhileInProgress}
              >
                Cancel
              </Button>
            </Group>
          </div>
        );
        break;

      case TransactionState.COMPLETE:
        const { payment } = this.state;
        const isPartiallyApproved = !payment.amount.isEqualTo(amount);

        footer = (
          <div className="quick-pay-modal__footer quick-pay-modal__footer--centered">
            <Group>
              {!isPartiallyApproved && autoFinishSale ? (
                <Button
                  variant="primary"
                  fill
                  onClick={this.handlePaymentComplete}
                  loading={syncingWithServer}
                >
                  Finish Sale
                </Button>
              ) : (
                <Button
                  variant="primary"
                  data-test="backToSale"
                  fill
                  onClick={this.handlePaymentComplete}
                >
                  Back to Sale
                </Button>
              )}
            </Group>
          </div>
        );
        break;

      case TransactionState.PRE_AUTHORIZED:
        if (Modules.hasModule('payments.pre_auth')) {
          footer = (
            <div className="quick-pay-modal__footer quick-pay-modal__footer--centered">
              <Group>
                <Button
                  variant="primary"
                  data-test="backToSale"
                  fill
                  onClick={this.handlePaymentComplete}
                  loading={syncingWithServer}
                >
                  Continue
                </Button>
              </Group>
            </div>
          );
        }
        break;

      case TransactionState.CANCELED:
      case TransactionState.CANCELLED_FROM_POS:
      case TransactionState.CONNECTION_KO:
      case TransactionState.DECLINED:
      case TransactionState.ERROR:
      case TransactionState.FAILED:
      case TransactionState.TERMINAL_ERROR:
        footer = (
          <div className="quick-pay-modal__footer">
            <Group>
              <Button
                data-test="terminal-payment-modal-close-button"
                onClick={this.handleCloseClicked}
              >
                Close
              </Button>
              {this.state.showRetryButton && (
                <Button
                  variant="primary"
                  data-test="terminal-payment-modal-retry-button"
                  fill
                  onClick={this.handleRetryPaymentClicked}
                >
                  Retry
                </Button>
              )}
            </Group>
          </div>
        );
        break;
      case TransactionState.CANCEL_UNAVAILABLE:
        footer = (
          <div className="quick-pay-modal__footer">
            <Group>
              <Button onClick={this.handleBackInProgress} data-test="ok-btn">
                OK
              </Button>
            </Group>
          </div>
        );
        break;
      case TransactionState.SERVICE_EXCEPTION:
      case TransactionState.NON_CANCELLABLE_STATE:
        footer = (
          <div className="quick-pay-modal__footer">
            <Group>
              <Button onClick={this.handleCloseClicked}>Close</Button>
            </Group>
          </div>
        );
        break;

      case TransactionState.CANCEL_FROM_TERMINAL:
        footer = (
          <div className="quick-pay-modal__footer quick-pay-modal__footer--centered">
            <Group>
              <Button onClick={this.handleBackToInProgress}>OK</Button>
            </Group>
          </div>
        );
        break;

      case TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND:
        footer = (
          <div className="quick-pay-modal__footer quick-pay-modal__footer--centered">
            <Group>
              <Button
                data-test="cancel-btn"
                disabled={this.state.disableCancelButton}
                onClick={this.handleCancelWhileInProgress}
              >
                Cancel
              </Button>
            </Group>
          </div>
        );
        break;

      case TransactionState.WAITING_TO_START:
        const { isLoading } = this.state;
        const disableStartTransaction = !selectedTerminal;

        footer = (
          <div className="quick-pay-modal__footer">
            <Group className="cr-justify-end">
              <Button onClick={this.handleCancelClicked}>Cancel</Button>
              <Button
                variant="primary"
                fill
                onClick={this.handleStartPaymentClicked}
                disabled={disableStartTransaction || isLoading}
              >
                Start Payment
              </Button>
            </Group>
          </div>
        );
        break;

      default:
        break;
    }

    return <ModalFooter>{footer}</ModalFooter>;
  }

  /**
   * The React render method.
   *
   * @returns {node}  The rendered QuickPayCashModal
   */
  render() {
    const { isOpen, transactionState } = this.state;

    const header = this.renderHeader();
    const body = this.renderBody();
    const footer = this.renderFooter();

    const allowCloseWithEsc =
      transactionState !== TransactionState.IN_PROGRESS &&
      transactionState !== TransactionState.COMPLETE &&
      transactionState !== TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND;

    return (
      <Modal
        className="add-deposit"
        isOpen={isOpen}
        onRequestClose={this.handleCloseModal}
        closeOnEsc={allowCloseWithEsc}
        shouldCloseOnOverlayClick={false}
      >
        {header}
        {body}
        {footer}
      </Modal>
    );
  }
}

TerminalPaymentModal.propTypes = {
  analyticsName: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onPaymentComplete: PropTypes.func,
  initialAmount: PropTypes.instanceOf(Decimal).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  terminal: PropTypes.shape(PropTypes.any.object),
  // eslint-disable-next-line react/forbid-prop-types
  paymentProvider: PropTypes.shape(PropTypes.any.object),
  // eslint-disable-next-line react/forbid-prop-types
  saleManager: PropTypes.shape(PropTypes.any.object).isRequired,
  canAmountBeChanged: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  recoveredPayment: PropTypes.shape(PropTypes.any.object),
  isManualEntry: PropTypes.bool,
  isUnreferencedRefund: PropTypes.bool,
  refundedPaymentID: PropTypes.string,
  captureMethod: PropTypes.string,
};

TerminalPaymentModal.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  analyticsName: 'Terminal',
  terminal: null,
  onPaymentComplete: Promise.resolve(),
  canAmountBeChanged: false,
  recoveredPayment: null,
  isManualEntry: false,
  isUnreferencedRefund: false,
  refundedPaymentID: null,
  captureMethod: null,
};

export default TerminalPaymentModal;
